import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CopyButton from "@/components/CopyButton";

// Owner settings for PhonePe Payment Gateway (Standard Checkout). When set up,
// "Pay online" at checkout sends customers to PhonePe's payment page; money
// settles to the bank account linked to the owner's PhonePe business account.
export default function PhonePeCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    client_id: "", client_secret: "", client_version: "1", environment: "production",
    webhook_username: "", webhook_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const apply = (data) => {
    setCfg(data);
    setForm({
      client_id: data.client_id || "",
      client_secret: "",
      client_version: String(data.client_version || 1),
      environment: data.environment || "production",
      webhook_username: data.webhook_username || "",
      webhook_password: "",
    });
  };

  useEffect(() => {
    api.get("/store/phonepe-config").then(({ data }) => apply(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.client_id.trim()) return toast.error("Enter your PhonePe Client ID.");
    if (!cfg?.secret_set && !form.client_secret.trim()) return toast.error("Enter your PhonePe Client Secret.");
    setSaving(true);
    try {
      const { data } = await api.put("/store/phonepe-config", {
        ...form,
        client_version: parseInt(form.client_version, 10) || 1,
      });
      apply(data);
      toast.success("PhonePe settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save PhonePe settings");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.get("/store/phonepe-config/test");
      toast.success(data.message || "PhonePe keys work.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PhonePe rejected these keys.");
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove PhonePe keys? Checkout goes back to Razorpay (if set up).")) return;
    await api.delete("/store/phonepe-config").catch(() => {});
    const { data } = await api.get("/store/phonepe-config");
    apply(data);
    toast.success("PhonePe keys removed");
  };

  const webhookUrl = cfg ? cfg.webhook_url || `${BACKEND_URL}${cfg.webhook_path}` : "";

  return (
    <Card className="border-slate-200 shadow-none mt-6" data-testid="phonepe-card">
      <CardHeader className="border-b">
        <CardTitle className="font-heading text-lg flex items-center gap-2 flex-wrap">
          <ShieldCheck className="w-5 h-5 text-violet-700" />
          PhonePe Payment Gateway
          {cfg?.configured && (
            <span className="mono text-[10px] uppercase text-emerald-700 flex items-center gap-1 ml-2">
              <ShieldCheck className="w-3 h-3" /> configured · {cfg.environment}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs text-slate-500 leading-relaxed">
          From your PhonePe Business dashboard (Developer Settings / API keys), copy the{" "}
          <b>Client ID</b>, <b>Client Secret</b> and <b>Client Version</b>. Use <b>Sandbox</b> with test keys
          first, then <b>Live</b> once PhonePe has approved your account. When PhonePe is set up, customers
          who choose &ldquo;Pay online&rdquo; pay on PhonePe&apos;s page (UPI, cards, net banking).
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Client ID</Label>
            <Input value={form.client_id} onChange={set("client_id")} className="mono text-xs"
              placeholder="SU2510..." data-testid="phonepe-client-id" />
          </div>
          <div className="space-y-1.5">
            <Label>Client Secret</Label>
            <Input type="password" autoComplete="new-password" value={form.client_secret}
              onChange={set("client_secret")} className="mono text-xs"
              placeholder={cfg?.secret_set ? "•••••••• (leave blank to keep saved secret)" : "Paste your client secret"}
              data-testid="phonepe-client-secret" />
          </div>
          <div className="space-y-1.5">
            <Label>Client Version</Label>
            <Input type="number" min="1" value={form.client_version} onChange={set("client_version")}
              className="mono text-xs" data-testid="phonepe-client-version" />
          </div>
          <div className="space-y-1.5">
            <Label>Mode</Label>
            <select value={form.environment} onChange={set("environment")}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="phonepe-environment">
              <option value="production">Live (real payments)</option>
              <option value="sandbox">Sandbox (testing)</option>
            </select>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 p-3 space-y-3">
          <div className="text-sm font-medium">Webhook (recommended)</div>
          <div className="text-xs text-slate-500">
            Confirms payments even if the customer closes the page before returning to your shop. In the
            PhonePe dashboard add a webhook with this URL, choose a username and password, and tick the
            <span className="mono"> checkout.order.completed</span> and{" "}
            <span className="mono">checkout.order.failed</span> events. Enter the same username and password here.
          </div>
          {webhookUrl && (
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded bg-slate-50 px-2 py-1.5 text-[11px]">{webhookUrl}</code>
              <CopyButton value={webhookUrl} label="Copy URL" />
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <Input value={form.webhook_username} onChange={set("webhook_username")} placeholder="Webhook username"
              className="text-xs" data-testid="phonepe-webhook-username" />
            <Input type="password" autoComplete="new-password" value={form.webhook_password}
              onChange={set("webhook_password")}
              placeholder={cfg?.webhook_password_set ? "•••••••• (leave blank to keep)" : "Webhook password"}
              className="text-xs" data-testid="phonepe-webhook-password" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="phonepe-save">
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving..." : "Save PhonePe settings"}
          </Button>
          <Button variant="outline" onClick={test} disabled={testing || !cfg?.configured} data-testid="phonepe-test">
            {testing ? "Testing..." : "Test connection"}
          </Button>
          {cfg?.configured && (
            <Button variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700" data-testid="phonepe-clear">
              Remove keys
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
